import { Box, TextField, Typography } from '@mui/material'
import React, { useContext, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoaderData } from 'react-router-dom'
import DeviceSelectionTable, {
  type DeviceData,
  type ValidationConfig,
} from '@/components/DeviceSelectionTable'
import {
  DefaultFetchDeviceFilters,
  DurationTestStatus,
  TransferState,
} from '@/api/definition'
import { layoutStyles } from '@/utils/theme'
import ConfirmDialog from '@/components/ConfirmDialogBox'
import { labelSx } from '@/styles/MuiComponentStyles'
import type { StartTestLoaderData } from '../loaders'
import { SelectedDevicesContext } from './SelectedDevicesContext'
import { useDeviceTypeFilters } from '@/hooks/useTransalationForFilter'
import { Device } from '@/pages/Device/Device'
import { enqueueSnackbar } from 'notistack'
interface RowSelectableParams {
  row: Device
  id?: string | number
  [key: string]: unknown
}
const SelectDevicesForDurationTestStep: React.FC = () => {
  const { t } = useTranslation(['app'])
  const context = useContext(SelectedDevicesContext)
  const loaderData = useLoaderData<StartTestLoaderData>()

  const [open, setOpen] = useState(false)
  const [remarks, setRemarks] = useState('')
  const [error, setError] = useState(false)

  if (!context) {
    throw new Error(
      'SelectDevicesForDurationTestStep must be used within SelectedDevicesContext',
    )
  }

  const { state, dispatch, onStartDurationTest, onSkipDurationTest } = context

  // Device filters matching V1 implementation
  const deviceFilters = useMemo(
    () => ({
      ...DefaultFetchDeviceFilters,
      inService: true,
      transferStatus: [TransferState.DOMESTIC],
      durationTestStatus: [DurationTestStatus.DurationTestNotStarted],
      selfTest: 1,
    }),
    [],
  )
  // Memoized to Prevent unnecessary re-renders
  const handleDeviceSelection = useCallback(
    (newlySelected: DeviceData[], deselected: string[]) => {
      const currentSelected = new Map(
        state.selectedDevices.map((device) => [device.uid, device]),
      )

      for (const device of newlySelected) {
        currentSelected.set(device.uid, device as Device)
      }

      for (const uid of deselected) {
        currentSelected.delete(uid)
      }

      dispatch({
        type: 'SET_SELECTED_DEVICES',
        payload: Array.from(currentSelected.values()),
      })
    },
    [state.selectedDevices, dispatch],
  )

  // Stable reference
  const handleNext = useCallback(async () => {
    await onStartDurationTest()
  }, [onStartDurationTest])

  const handleSkip = useCallback(async () => {
    if (remarks.trim() === '') {
      setError(true)
      setOpen(true)
      return
    }
    setError(false)
    await onSkipDurationTest()
  }, [remarks, onSkipDurationTest])

  const handleSkipClick = useCallback(() => {
    setOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
    setError(false)
  }, [])

  const filterConfig = useDeviceTypeFilters()

  // Selected device IDs
  const selectedDeviceIds = useMemo(
    () => state.selectedDevices.map((device) => device.uid),
    [state.selectedDevices],
  )
  const validateDeviceEditable = useCallback(
    (device: Device): boolean => {
      if (device.editable) {
        return true
      }
      enqueueSnackbar(t('di:device.rights.producerOnlyError'), {
        variant: 'error',
      })
      return false
    },
    [t],
  )
  // Validation configuration
  const validationConfig: ValidationConfig = useMemo(
    () => ({
      validateSelection: validateDeviceEditable,
      isRowSelectable: (params: RowSelectableParams) => params.row.editable,
      validationErrorMessage: t('di:device.deviceCannotBeEdited', {
        count: '{count}',
      }),
    }),
    [validateDeviceEditable, t],
  )
  return (
    <Box sx={{ ...layoutStyles.pageContainer, mt: 0 }}>
      <DeviceSelectionTable
        initialDevices={loaderData?.availableDevices || []}
        rowCount={loaderData?.rowCount || 0}
        selectedDeviceIds={selectedDeviceIds}
        onSelectionChange={handleDeviceSelection}
        onRowClick={validateDeviceEditable}
        validationConfig={validationConfig}
        onNext={handleNext}
        deviceFilters={deviceFilters}
        loaderError={loaderData?.error}
        leftButtonProps={{
          text: t('common:general.back'),
          variant: 'contained',
          color: 'inherit',
          disabled: true,
          onClick: () => {},
        }}
        extraButtonProps={{
          text: t('device.durationTest.skipDurationTest'),
          tooltip: t('device.durationTest.skipDurationTest'),
          color: 'warning',
          variant: 'outlined',
          sx: {
            backgroundColor: '#9c27b0',
            color: '#fff',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#8e24aa',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              backgroundColor: '#9c27b0',
              color: '#fff',
              opacity: 0.65,
            },
          },
          disabled: state.selectedDevices.length === 0,
          onClick: handleSkipClick,
        }}
        nextButtonProps={{
          text: t('device.durationTest.startDurationTest'),
          tooltip: t('device.durationTest.startDurationTest'),
          color: 'success',
          disabled: state.selectedDevices.length === 0,
        }}
        filterConfig={filterConfig}
        sharingConfig={{
          shareFilters: true,
          shareSearch: true,
          enableFilters: true,
          enableSearch: true,
        }}
      />
      <ConfirmDialog
        open={open}
        setOpen={setOpen}
        onConfirm={handleSkip}
        onCancel={handleClose}
        title={t('common:general.areYouSure')}
        confirmText={t('device.durationTest.skipDurationTest')}
        cancelText={t('common:general.cancel')}
      >
        <Typography variant="subtitle1">
          {t(
            'device.durationTest.confirmSkipDurationTest',
            'Are you sure you want to skip the duration test?',
          )}
        </Typography>
        <TextField
          error={error && remarks === ''}
          fullWidth
          id="remarks"
          required
          label={t('common:general.remarks')}
          value={remarks}
          onChange={(event) => setRemarks(event.target.value)}
          helperText={
            error && remarks === '' && t('device.durationTest.remarksRequired')
          }
          sx={{ mt: 2 }}
          variant="standard"
          size="small"
          slotProps={{ inputLabel: { sx: labelSx, shrink: true } }}
        />
      </ConfirmDialog>
    </Box>
  )
}

export default SelectDevicesForDurationTestStep
